import { Button, Input } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useAuth } from "@/shared/session";
import { activityLabelSchema } from "../schemas/activityLabelSchema";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { Field } from "@/ui/chakra/field";

export type ActivityLabelFormInput = z.infer<typeof activityLabelSchema>;

interface CreateActivityLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ActivityLabelFormInput) => void;
  isSubmitting?: boolean;
}

export const CreateActivityLabelModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}: CreateActivityLabelModalProps) => {
  const { session } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ActivityLabelFormInput>({
    resolver: zodResolver(activityLabelSchema),
    defaultValues: {
      label: "",
      zonesIds: [session?.zone ?? ""],
      enabled: true,
    },
  });

  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen, reset]);

  const onFormSubmit = (data: ActivityLabelFormInput) => {
    onSubmit(data);
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} isCentered size="md">
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Crear etiqueta de actividad</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Field
              label="Nombre"
              required
              invalid={!!errors.label}
              errorText={errors.label?.message}
            >
              <Input
                {...register("label")}
                placeholder="Nombre"
                autoComplete="off"
              />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isSubmitting}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
